import { useCallback, useEffect, useState } from "react";
import { LogIn, LogOut, User } from "lucide-react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { OwlMascot } from "./components/OwlMascot";
import { FromNyvo } from "./sections/FromNyvo";
import { ChatWithOwl } from "./sections/ChatWithOwl";
import { AboutOwl } from "./sections/AboutOwl";
import { ChatDrawer } from "./sections/ChatDrawer";
import { IdentifyModal } from "./sections/IdentifyModal";
import { SessionProvider, useSession } from "./lib/SessionContext";

/**
 * Owl — the daily market voice. Simple three-zone home:
 *   1. bento cards (today's 5 witty, jargon-free insights)
 *   2. chat with Owl (funds + market Q&A — compare, direction, track record
 *      all live IN the chat, not as page furniture)
 *   3. what is Owl
 *
 * 2026-06-20 — Phase 1 rebuild. Replaces the old magazine briefing + waitlist
 * shell with the newsroom feed. No auto-popup (felt like a paywall); sign-in
 * stays available for chat history.
 *
 * 2026-07-06 — CLEAN PAGE (founder call). The Phase-3/4 analytical panels
 * (DirectionalRead + Convergence + AnalogOutlook, Calibration "How Owl keeps
 * score", the FundCompare grid) are UNMOUNTED: the page is 5 insights + chat,
 * full stop. Chat answers all of it on demand — a "Compare two funds" starter
 * chip replaces the grid. Components stay in src/sections for reuse.
 */
export default function App() {
  return (
    <SessionProvider>
      <Shell />
    </SessionProvider>
  );
}

function Shell() {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatPrefill, setChatPrefill] = useState<string | null>(null);
  const [identifyOpen, setIdentifyOpen] = useState(false);

  // Keyboard: '/' opens chat.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (
        e.key === "/" &&
        document.activeElement?.tagName !== "INPUT" &&
        document.activeElement?.tagName !== "TEXTAREA"
      ) {
        e.preventDefault();
        setChatPrefill(null);
        setChatOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openChat = useCallback((prefill?: string) => {
    setChatPrefill(prefill ?? null);
    setChatOpen(true);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Topbar
        onOpenSignIn={() => setIdentifyOpen(true)}
        onOpenChat={() => openChat()}
      />

      <main className="mx-auto max-w-6xl px-4 pb-32 pt-6 sm:px-6 lg:px-8">
        <ErrorBoundary label="from-nyvo">
          <FromNyvo onOpenChat={openChat} />
        </ErrorBoundary>

        <ErrorBoundary label="chat-with-owl">
          <ChatWithOwl onOpenChat={openChat} />
        </ErrorBoundary>

        <ErrorBoundary label="about">
          <AboutOwl />
        </ErrorBoundary>

        <Footer />
      </main>

      {/* ChatDrawer renders its own floating "Ask Owl" launcher when closed. */}
      <ChatDrawer open={chatOpen} onOpenChange={setChatOpen} prefill={chatPrefill} />
      <IdentifyModal
        open={identifyOpen}
        onClose={() => setIdentifyOpen(false)}
        context="explicit"
      />
    </div>
  );
}

/* ────────────────────── topbar ────────────────────── */

function Topbar({
  onOpenSignIn,
  onOpenChat,
}: {
  onOpenSignIn: () => void;
  onOpenChat: () => void;
}) {
  const { status, email, signOut } = useSession();
  return (
    <div className="sticky top-3 z-30 mx-auto max-w-6xl px-3 sm:px-4 lg:px-6">
      <header className="pill-nav flex items-center justify-between gap-6 px-4 py-2.5 sm:px-6">
        <a href="#today" className="flex items-center gap-3">
          <OwlMascot size="nav" />
          <div className="flex items-center gap-2 leading-none">
            <span className="text-lg font-extrabold tracking-tight text-nyvo-900">
              Owl
            </span>
            <span className="rounded-full bg-sky-50 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-sky-700">
              Beta
            </span>
          </div>
        </a>

        <nav className="hidden items-center gap-7 font-mono text-[12px] text-slate-600 lg:flex">
          <a href="#today" className="transition hover:text-nyvo-900">Today</a>
          <a href="#chat" className="transition hover:text-nyvo-900">Chat</a>
          <a href="#about" className="transition hover:text-nyvo-900">About</a>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={onOpenChat}
            className="hidden rounded-full px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:text-sky-700 sm:inline-flex"
          >
            Ask Owl
          </button>
          {status === "signed_in" && email ? (
            <UserPill email={email} onSignOut={signOut} />
          ) : status === "anonymous" ? (
            <button
              type="button"
              onClick={onOpenSignIn}
              aria-label="Continue with email"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:text-sky-700"
            >
              <LogIn className="h-3 w-3" aria-hidden />
              Continue with email
            </button>
          ) : null}
        </div>
      </header>
    </div>
  );
}

function UserPill({
  email,
  onSignOut,
}: {
  email: string;
  onSignOut: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const display = email.length > 22 ? email.slice(0, 19) + "…" : email;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs text-nyvo-900 transition hover:border-sky-300"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <User className="h-3 w-3 text-sky-700" aria-hidden />
        <span className="hidden font-mono sm:inline">{display}</span>
        <span className="font-mono sm:hidden">{display.split("@")[0]}</span>
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div className="absolute right-0 top-full z-20 mt-1 w-56 rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
            <div className="border-b border-slate-100 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">
                Signed in as
              </div>
              <div className="mt-0.5 break-all font-mono text-xs text-nyvo-900">
                {email}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void onSignOut();
              }}
              className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-xs text-slate-700 transition hover:bg-slate-50"
            >
              <LogOut className="h-3 w-3" aria-hidden />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Footer() {
  return (
    <footer className="mt-14 border-t border-slate-200 pt-6 text-center">
      <p className="mx-auto max-w-2xl text-[11px] leading-relaxed text-slate-500">
        <span className="font-semibold text-nyvo-900">
          General information, not personal advice.
        </span>{" "}
        Owl is Owl's daily market commentary and a research utility for NYVO's
        mutual-fund ratings. It is general in nature, not tailored to your
        goals, income, taxes, or risk appetite, and is not a recommendation to
        buy, sell, or hold any security or fund. Views are NYVO's independent,
        model-and-data-driven reading, can be wrong or out of date, and may
        change as new data arrives. Mutual fund investments are subject to
        market risks; read all scheme-related documents carefully. Past
        performance is not indicative of future returns.
      </p>
      <p className="mx-auto mt-4 max-w-2xl text-[12px] leading-relaxed text-slate-600">
        For any feedback, write to{" "}
        <a
          href="mailto:harsh@nyvo.in"
          className="font-semibold text-sky-700 hover:underline"
        >
          harsh@nyvo.in
        </a>
        .
      </p>
      <p className="mt-3 text-[10px] text-slate-400">
        © {new Date().getFullYear()} NYVO Technology Private Limited ·{" "}
        <a href="https://nyvo.money" className="hover:text-sky-700 hover:underline">
          nyvo.money
        </a>
      </p>
    </footer>
  );
}
